import * as React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import classnames from 'classnames';

import { navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import SortableTable from 'components/sortable-table/SortableTable';
import PageSection from 'components/page/page-section/PageSection';

import { getShortlistedUISettings } from 'selectors/participants';

import { removeSubcampaign } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { downloadIndividualOffer } from 'modules/campaigns/reducers/campaignIndividualOffer';
import { copyOfferDetailsWithDeliverables } from 'modules/subcampaigns/reducers/offerDetailsBuffer';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { success } from 'redux-store/reducers/common-reducer';
import { fetchOverdueDeliverables, resetOverdueDeliverables } from 'modules/subcampaigns/reducers/overdueDeliverables';
import { saveCurrentSelectedSubcampaigns } from '../../reducers/bulkOffer';

import {
  EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED,
  EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED_ALL,
  EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_FOOTER_CLICKED,
  EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_ROW_CLICKED,
  EVENT_TYPE_OFFERS_TABLE_CREATE_MANY_BUTTON_IN_FOOTER_CLICKED
} from 'modules/tracking/utils/tracking';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import {
  BULK_TYPE_CREATE_OFFERS,
  getBulkOfferSelectedSubcampaigns,
  getSelectedSubcampaigns
} from 'utils/bulk-offer';
import {
  isDraft,
  isOfferDraftAndBlank,
  isOfferSavedAsDraft,
  isRevoked
} from 'utils/subcampaign';
import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { getVariablePaymentsById, sumVariablePayments } from 'utils/payouts';
import { enableNavigation } from 'utils/prompt-exit';
import { logError } from 'utils/log';
import {
  getCampaignContentType,
  getInfluencerAvailableDeliverableChannels,
  getOffersUniqTypes
} from 'utils/deliverables';
import { canCampaignBeChanged } from 'utils/campaign';

import { getCheckboxColumn, getColumnsBySubcampaignState } from './subcampaigns-table-columns';

import './subcampaigns-table.scss';

import type {
  Campaign,
  CampaignOverview,
  CampaignParticipantsColumnsVisibilityState,
  DeliverablesOverdueBySubcampaignIdLocal,
  Dispatch,
  MessengerNotifications,
  ResponseTime,
  SortableTableSort,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignOfferDetailsBuffer,
  SubcampaignSelectedIdsMap,
  SubcampaignState,
  VariablePayments
} from 'types';
import type { SubcampaignTableActions, SubcampaignTableItem } from './subcampaigns-table-columns';

applyTranslations(require('./subcampaigns-table.i18n.json'));
applyTranslations(require('modules/subcampaigns/components/copy/subcampaign-offer-copy-paste.i18n.json'));

type OwnProps = {
  campaign: Campaign,
  defaultSortingKey: string,
  defaultSortingDirection: 'ascending' | 'descending',
  subcampaign: Subcampaign | undefined | null,
  subcampaignState: SubcampaignState,
  subcampaigns: Array<Subcampaign>,
  responseTimeByInfluencer: {
    [x: number]: ResponseTime
  },
  variablePayments?: VariablePayments | null,
  overview: CampaignOverview,
  messengerNotifications: MessengerNotifications,
  subcampaignOfferSortState?: string,
  onSortChanged: (a: SortableTableSort) => void,
  isContentApprovalV2: boolean
};

type StateProps = {
  bulkOffer: SubcampaignBulkOfferStore,
  isEditMode: boolean,
  isDraftOpenAction: boolean,
  copiedOfferDetails: SubcampaignOfferDetailsBuffer | undefined | null,
  campaignUISettingsShorlisted: CampaignParticipantsColumnsVisibilityState,
  overdueDeliverables: DeliverablesOverdueBySubcampaignIdLocal
};

type DispatchProps = {
  dispatchSaveCurrentSelectedSubcampaigns: (selectedIdsMap: SubcampaignSelectedIdsMap) => void,
  dispatchDownloadOffer: (subcampaign: Subcampaign) => void,
  dispatchRemoveFromShortlist: (subcampaign: Subcampaign, campaign: Campaign) => void,
  dispatchFetchOverdueDeliverables: (overdueDeliverableIds: Array<number>) => void,
  dispatchResetOverdueDeliverables: () => void,
  dispatch: Dispatch
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  selectedIdsMap: SubcampaignSelectedIdsMap
};

const getSubcampaignEditLink = (campaignId, subcampaignId) => `/campaigns/${campaignId}/subcampaigns/${subcampaignId}`;

class SubcampaignsTable extends React.PureComponent<Props, State> {
  state = {
    selectedIdsMap: {
      ...this.props.bulkOffer.selectedIdsMap
    }
  };

  componentDidMount() {
    const { subcampaign, isDraftOpenAction } = this.props;

    if (subcampaign && isDraft(subcampaign)) {
      // If an user try to access direct by url
      if (isDraftOpenAction) {
        this.handleDeselectAllButOne(subcampaign.id);
      } else {
        this.enableCurrentSubcampaign(subcampaign.id);
      }
    }

    this.fetchOverdueDeliverables();
  }

  async componentDidUpdate(prevProps: Props) {
    const { subcampaign, bulkOffer, isDraftOpenAction } = this.props;

    // If an user try to access direct by url
    if (subcampaign && subcampaign !== prevProps.subcampaign && isDraft(subcampaign)) {
      if (isDraftOpenAction) {
        await this.handleDeselectAllButOne(subcampaign.id);
      } else {
        this.enableCurrentSubcampaign(subcampaign.id);
      }
    }

    // use redux state as source of truth
    if (!isEqual(this.state.selectedIdsMap, bulkOffer.selectedIdsMap)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        selectedIdsMap: {
          ...this.props.bulkOffer.selectedIdsMap
        }
      });
    }
  }

  componentWillUnmount() {
    this.props.dispatchResetOverdueDeliverables();
  }

  fetchOverdueDeliverables(): void {
    const { subcampaigns, dispatchFetchOverdueDeliverables } = this.props;
    const subcampaignIds = (subcampaigns || [])
      .filter(s => !isRevoked(s)).map(subcampaign => subcampaign.id);

    dispatchFetchOverdueDeliverables(subcampaignIds);
  }

  enableCurrentSubcampaign(subcampaignId: number) {
    this.setState(state => ({
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaignId]: true
      }
    }), this.persistChanges);
  }

  getSelectedSubcampaigns = () => getBulkOfferSelectedSubcampaigns(this.props.bulkOffer, this.state.selectedIdsMap);

  areAllSelected() {
    const { subcampaigns } = this.props;

    return this.getSelectedSubcampaigns().length === subcampaigns.length;
  }

  persistChanges = (isFromTwoToOne: boolean = false): void => {
    const {
      campaign,
      dispatchSaveCurrentSelectedSubcampaigns,
      isEditMode
    } = this.props;
    const { selectedIdsMap } = this.state;

    dispatchSaveCurrentSelectedSubcampaigns(selectedIdsMap);

    const selectedSubcampaigns = this.getSelectedSubcampaigns();

    if (!selectedSubcampaigns.length) {
      navigate.to(`/campaigns/${campaign.id}/participants/shortlisted`);
    } else if (isFromTwoToOne && isEditMode) {
      const subcampaign = selectedSubcampaigns[0];
      const { campaignId, id } = subcampaign;

      navigate.to(`/campaigns/${campaignId}/subcampaigns/${id}/edit?new=1&bulk=create_offers`);
    }
  };

  dispatchTrackEvent(eventType: string): void {
    addUserAction({
      eventCategory: 'offers-table',
      eventAction: eventType
    });
  }

  getSelectedIds(): Array<number> {
    const { selectedIdsMap } = this.state;

    return Object.keys(selectedIdsMap)
      .map(key => {
        const id = parseInt(key, 10);
        if (selectedIdsMap[id]) {
          return id;
        }

        return -1;
      })
      .filter(id => id !== -1);
  }

  handleToggle = (subcampaignId: number): void => {
    const beforeCount = this.getSelectedIds().length;

    this.setState(state => ({
      ...state,
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaignId]: !state.selectedIdsMap[subcampaignId]
      }
    }), () => {
      const afterCount = this.getSelectedIds().length;
      this.persistChanges(beforeCount === 2 && afterCount === 1);
    });


    this.dispatchTrackEvent(EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED);
  };

  isSubcampaignEligibleForBulkOffer(subcampaign: Subcampaign): boolean {
    const { bulkOffer } = this.props;
    const channels = getInfluencerAvailableDeliverableChannels(subcampaign.creatorProfile);

    return bulkOffer.contentTypes.length === 0
      || bulkOffer.contentTypes.every(type => channels.includes(type));
  }

  handleToggleAll = (): void => {
    const { subcampaigns } = this.props;
    const selectedIdsMap = {};

    const areAllSelected = this.areAllSelected();

    // on deselected all -> redirect on shortlisted
    subcampaigns.forEach(subcampaign => {
      selectedIdsMap[subcampaign.id] = !areAllSelected;

      if (!this.isSubcampaignEligibleForBulkOffer(subcampaign)) {
        selectedIdsMap[subcampaign.id] = false;
      }
    });

    this.setState(state => ({
      ...state,
      selectedIdsMap
    }), this.persistChanges);

    this.dispatchTrackEvent(EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED_ALL);
  };

  handleDeselectAllButOne = (selectedId: number): Promise<void> => new Promise((resolve) => {
    this.setState(state => {
      const newIdsMap = {};
      Object.keys(state.selectedIdsMap).forEach(key => {
        newIdsMap[key] = false;
      });
      newIdsMap[String(selectedId)] = true;

      return {
        ...state,
        selectedIdsMap: newIdsMap
      };
    }, () => {
      this.persistChanges();
      resolve();
    });
  });

  handleToggleOffer = ({
    subcampaign
  }: {
    subcampaign: Subcampaign
  }) => {
    this.handleToggle(subcampaign.id);
  };

  navigateToOffer = ({
    campaign,
    subcampaign
  }: SubcampaignTableItem) => {
    navigate.to(getSubcampaignEditLink(campaign.id, subcampaign.id));
  };

  handleCopyOfferDetails = (subcampaign: Subcampaign): void => {
    const { dispatch } = this.props;
    const offer = subcampaign.updatedOffer || subcampaign.offer;
    dispatch(copyOfferDetailsWithDeliverables(offer));
    dispatch(success(__t('subcampaign_offer_copy_paste.offer_details_copied_merged_page')));
  };

  handlePasteOfferInvoke = (subcampaign: Subcampaign, campaign: Campaign): void => {
    const { dispatch } = this.props;

    if (isOfferDraftAndBlank(subcampaign.offer)) {
      navigate.to(`/campaigns/${campaign.id}/subcampaigns/${subcampaign.id}/edit?paste=active`);
      return;
    }

    dispatch(showConfirmModal({
      title: __t('subcampaign_offer_copy_paste.confirmation_modal_title'),
      message: __t('subcampaign_offer_copy_paste.confirmation_modal_description'),
      onConfirm: () => {
        dispatch(hideConfirmModal());
        enableNavigation();
        navigate.to(`/campaigns/${campaign.id}/subcampaigns/${subcampaign.id}/edit?new=1&paste=active`);
      },
      onCancel: () => dispatch(hideConfirmModal())
    }));
  };

  unselectMultipleExceptActive = async (activeId: number) => {
    const { dispatch } = this.props;

    await this.handleDeselectAllButOne(activeId);
    dispatch(success(__t('subcampaigns_table.tooltips.shortlisted_offer_reselect')));
  };

  handlePasteOfferInvokeWithUnchecking = (subcampaign: Subcampaign, campaign: Campaign): void => {
    const { dispatch } = this.props;

    dispatch(showConfirmModal({
      title: __t('subcampaign_offer_copy_paste_prepare.confirmation_modal_title'),
      message: __t('subcampaign_offer_copy_paste_prepare.confirmation_modal_description'),
      onConfirm: async () => {
        dispatch(hideConfirmModal());
        enableNavigation();

        try {
          await this.unselectMultipleExceptActive(subcampaign.id);
          this.handlePasteOfferInvoke(subcampaign, campaign);
        } catch (e) {
          logError(e);
        }
      },
      onCancel: () => dispatch(hideConfirmModal())
    }));
  };

  handlePasteCopiedOfferDetails = (subcampaign: Subcampaign, campaign: Campaign): void => {
    const selectedIds = this.getSelectedSubcampaigns().map(entity => entity.id);

    if (selectedIds.length === 0 || (selectedIds.length === 1 && selectedIds[0] === subcampaign.id)) {
      this.handlePasteOfferInvoke(subcampaign, campaign);
    } else {
      this.handlePasteOfferInvokeWithUnchecking(subcampaign, campaign);
    }
  };

  handleRemoveFromShortlist = (subcampaign: Subcampaign, campaign: Campaign) => {
    if (this.state.selectedIdsMap[subcampaign.id]) {
      enableNavigation();
    }

    this.props.dispatchRemoveFromShortlist(subcampaign, campaign);

    this.setState(state => ({
      ...state,
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaign.id]: false
      }
    }), this.persistChanges);
  };

  handleSpecifyOffer = (campaignId: number, subcampaignId: number) => {
    const { dispatch } = this.props;
    const navigateTo = `/campaigns/${campaignId}/subcampaigns/${subcampaignId}/edit?new=1&bulk=create_offers`;
    const selectedIds = this.getSelectedSubcampaigns().map(entity => entity.id);

    if (selectedIds.length === 0 || (selectedIds.length === 1 && selectedIds[0] === subcampaignId)) {
      navigate.to(navigateTo);
    } else {
      dispatch(showConfirmModal({
        title: __t('subcampaigns_table.specify_offer_prepare.confirmation_modal_title'),
        message: __t('subcampaigns_table.specify_offer_prepare.confirmation_modal_description'),
        onConfirm: async () => {
          dispatch(hideConfirmModal());
          enableNavigation();

          try {
            await this.unselectMultipleExceptActive(subcampaignId);
            // give extra time to show notification
            setTimeout(navigate.to, 500, navigateTo);
          } catch (e) {
            logError(e);
          }
        },
        onCancel: () => dispatch(hideConfirmModal())
      }));
    }

    this.dispatchTrackEvent(EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_ROW_CLICKED);
  };

  handleSpecifyOfferFromFooter = () => {
    const { campaign } = this.props;
    const selectedSubcampaigns = this.getSelectedSubcampaigns();

    navigate.to(`/campaigns/${campaign.id}/subcampaigns/${selectedSubcampaigns[0].id}/edit?new=1&bulk=${BULK_TYPE_CREATE_OFFERS}`);

    this.dispatchTrackEvent(
      selectedSubcampaigns.length > 1
        ? EVENT_TYPE_OFFERS_TABLE_CREATE_MANY_BUTTON_IN_FOOTER_CLICKED
        : EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_FOOTER_CLICKED
    );
  };

  getActions(subcampaign: Subcampaign): SubcampaignTableActions {
    const {
      isContentApprovalV2,
      copiedOfferDetails,
      dispatchDownloadOffer
    } = this.props;
    const isCA2CopiedOffer = copiedOfferDetails?.deliverables
      .some(deliverable => deliverable.contentApprovalV2) ?? false;
    const canPasteCopiedOffer = isContentApprovalV2 === isCA2CopiedOffer
      && copiedOfferDetails?.offer.id !== subcampaign.offer.id;

    return {
      onCopyOfferDetails: this.handleCopyOfferDetails,
      onPasteCopiedOfferDetails: canPasteCopiedOffer && copiedOfferDetails ? this.handlePasteCopiedOfferDetails : null,
      onDownloadOffer: dispatchDownloadOffer,
      onRemoveFromShortlist: this.handleRemoveFromShortlist,
      onSpecifyOffer: this.handleSpecifyOffer
    };
  }

  getColumnsByState() {
    const {
      campaignUISettingsShorlisted,
      subcampaigns,
      subcampaignState,
      overview,
      overdueDeliverables,
      subcampaignOfferSortState
    } = this.props;

    const variablePaymentSum = get(overview, `${subcampaignState}Overview.variablePaymentsSum`, 0);

    if (subcampaignState !== 'draft') {
      return getColumnsBySubcampaignState({ subcampaigns, subcampaignState, overdueDeliverables, variablePaymentSum, subcampaignOfferSortState });
    }

    return getColumnsBySubcampaignState({ subcampaigns, subcampaignState, overdueDeliverables })
      .filter(column => (
        column.customizable ? campaignUISettingsShorlisted.columns[column.key] : true
      ));
  }

  renderFooter() {
    const { bulkOffer, campaign, isEditMode } = this.props;
    const selectedSubcampaigns = this.getSelectedSubcampaigns();
    const selectedSubcampaignsCount = selectedSubcampaigns.length;
    const canEdit = canCampaignBeChanged(campaign);

    let footerMessage;
    let buttonLabel;

    if (!canEdit) {
      footerMessage = __t('subcampaigns_table.cant_create_offer_label');
    } else if (
      selectedSubcampaigns.length === 1 && !(selectedSubcampaigns[0]?.offer.isDefault ?? true)
    ) {
      footerMessage = __t('subcampaigns_table.continue_offer_creation_label');
      buttonLabel = __t('subcampaigns_table.buttons.continue_offer_creation');
    } else {
      footerMessage = (selectedSubcampaignsCount > 0)
        ? __t('subcampaigns_table.create_offer_label', { withPlural: selectedSubcampaignsCount })
        : __t('subcampaigns_table.select_to_create_offer_label');

      buttonLabel = (selectedSubcampaignsCount > 0)
        ? __t('subcampaigns_table.buttons.create_bulk_offer', { withPlural: selectedSubcampaignsCount })
        : __t('subcampaigns_table.buttons.create_offer');
    }

    const isSelectedOffersWereSavedAsDrafts = getSelectedSubcampaigns(
      bulkOffer.shortlistedSubcampaigns, bulkOffer.selectedIdsMap
    ).map(val => isOfferSavedAsDraft(val.offer)).every(v => v);

    if (isSelectedOffersWereSavedAsDrafts && selectedSubcampaignsCount > 1) {
      buttonLabel = __t('subcampaigns_table.buttons.overwrite_offer_drafts', {
        count: selectedSubcampaignsCount
      });
    }


    return (
      <div className="subcampaigns-table__footer">
        <div className="subcampaigns-table__footer-message">
          {footerMessage}
        </div>
        {
          canEdit && (
            <ActionButton
              color="primary"
              disabled={isEditMode || !selectedSubcampaignsCount}
              onClick={this.handleSpecifyOfferFromFooter}
            >
              {buttonLabel}
            </ActionButton>
          )
        }
      </div>
    );
  }

  render() {
    const {
      campaign,
      defaultSortingKey,
      defaultSortingDirection,
      subcampaign,
      subcampaigns,
      subcampaignState,
      campaignUISettingsShorlisted,
      responseTimeByInfluencer,
      variablePayments,
      messengerNotifications,
      onSortChanged
    } = this.props;

    const { selectedIdsMap } = this.state;
    const canEdit = canCampaignBeChanged(campaign);
    const isDraftState = subcampaignState === 'draft';
    const hasCheckboxes = canEdit && isDraftState;
    const selectedSubcampaignsCount = this.getSelectedSubcampaigns().length;
    const isMultipleOfferCreationActive = selectedSubcampaignsCount > 1 && subcampaignState === 'draft';
    const offers = subcampaigns.map(entity => entity.offer);
    const campaignContentType = getCampaignContentType(offers);

    const data: Array<SubcampaignTableItem> = subcampaigns.map<SubcampaignTableItem>(entity => ({
      id: entity.id, // SortableTable key
      actions: this.getActions(entity),
      campaign,
      subcampaign: entity,
      subcampaignState,
      isMultipleOfferCreationActive,
      campaignContentType,
      taskChannelTypes: getOffersUniqTypes([entity.offer]),
      isSelected: selectedIdsMap[entity.id] || (!isDraftState && subcampaign && subcampaign.id === entity.id),
      isSelectable: this.isSubcampaignEligibleForBulkOffer(entity),
      hasUnreadMessages: Boolean(messengerNotifications.unreadChatsMap[entity.conversationExternalId]) || false,
      responseTime: responseTimeByInfluencer && responseTimeByInfluencer[entity.creatorProfile.id] || null,
      variablePaymentAmount: variablePayments && sumVariablePayments(getVariablePaymentsById('subcampaignId', entity.id, variablePayments.items)) || null
    }));

    const tableClasses = classnames('subcampaigns-table__table', {
      'subcampaigns-table__table--disable-instagram-metrics': isDraftState && !campaignUISettingsShorlisted.channels.instagram,
      'subcampaigns-table__table--disable-tiktok-metrics': isDraftState && !campaignUISettingsShorlisted.channels.tiktok,
      'subcampaigns-table__table--disable-youtube-metrics': isDraftState && !campaignUISettingsShorlisted.channels.youtube
    });

    let onRowClicked;

    if (isDraftState) {
      if (canEdit) {
        onRowClicked = this.handleToggleOffer;
      }
      // don't do anything if it's not possible to edit
    } else {
      onRowClicked = this.navigateToOffer;
    }

    return (
      <React.Fragment>
        <PageSection
          style="volume"
          padding="none"
          margin="none"
          customClass="subcampaigns-table__section"
        >
          <div className="subcampaigns-table__wrapper">
            <SortableTable
              maxVirtualPageSize={50}
              layout="dense"
              customClass={tableClasses}
              data={data}
              defaultSortingKey={defaultSortingKey}
              defaultSortingDirection={defaultSortingDirection}
              columns={[
                ...insertIf(hasCheckboxes, getCheckboxColumn({
                  isChecked: this.areAllSelected(),
                  onToggle: this.handleToggle,
                  onToggleAll: this.handleToggleAll
                })),
                ...this.getColumnsByState()
              ]}
              emptyMessage={__t(`subcampaigns_table.empty_message.${subcampaignState}`)}
              onRowClicked={onRowClicked}
              onSortChanged={onSortChanged}
            />
          </div>
        </PageSection>
        {isDraftState && this.renderFooter()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({
  bulkOffer,
  campaignUISettingsShorlisted,
  offerDetailsBuffer,
  overdueDeliverables,
  router
}): StateProps => ({
  bulkOffer,
  campaignUISettingsShorlisted: getShortlistedUISettings(campaignUISettingsShorlisted.visibility),
  copiedOfferDetails: offerDetailsBuffer.copiedOfferDetails,
  isEditMode: /\/edit$/.test(router.location.pathname),
  isDraftOpenAction: router.location?.query?.draft === 'open',
  overdueDeliverables
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveCurrentSelectedSubcampaigns: (selectedIdsMap: SubcampaignSelectedIdsMap) => dispatch(saveCurrentSelectedSubcampaigns(selectedIdsMap)),
  dispatchDownloadOffer: (subcampaign) => dispatch(downloadIndividualOffer(subcampaign.id, true)),
  dispatchRemoveFromShortlist: (subcampaign, campaign) => dispatch(removeSubcampaign(subcampaign, campaign.id)),
  dispatchFetchOverdueDeliverables: subcampaignIds => dispatch(fetchOverdueDeliverables(subcampaignIds)),
  dispatchResetOverdueDeliverables: () => dispatch(resetOverdueDeliverables()),
  dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignsTable);
